import { ParsedUrlQuery } from 'querystring';

import { ApiApi } from '@api';
import { filterPersistenceStore, viewSettingsStore } from '@stores';
import { ColumnFilter, ColumnFiltersState } from '@tanstack/react-table';
import isEqual from 'lodash.isequal';
import Router from 'next/router';
import { Dispatch, SetStateAction } from 'react';
import { toast } from 'react-toastify';

import styles from '../sections/ModelInventoryTable/ModelInventoryTable.module.scss';

const updateQuery = columnFilters => {
  const currentQuery = { ...Router.query };

  // Always remove 'redirection' if present
  delete currentQuery.redirection;

  // Create a new query object with only preserved parameters
  const finalQuery = {};
  const preservedParams = ['id', 'activeTab', 'model', 'assessment', 'concierge'];

  // First, preserve any non-filter parameters
  preservedParams.forEach(param => {
    if (currentQuery[param]) {
      finalQuery[param] = currentQuery[param];
    }
  });

  // Then add any active filters
  if (columnFilters?.length > 0) {
    columnFilters.forEach((filter: ColumnFilter) => {
      const filterValue = filter.value as string[];
      if (filterValue && filterValue.length > 0) {
        finalQuery[filter.id] = filterValue.join(',');
      }
    });
  }

  // A shallow push cancels any in-flight route change, so skip it when the query is already in sync —
  // otherwise the query-sync effects in the inventory tables re-trigger each other in an endless push loop
  if (!isEqual(finalQuery, Router.query)) {
    Router.push({ query: finalQuery }, undefined, {
      shallow: true,
    });
  }

  if (columnFilters !== null) {
    filterPersistenceStore.set(Router.pathname, columnFilters);
  }
};

const getFiltersFromUrl = (
  query: ParsedUrlQuery,
  defaultFilters: ColumnFilter[],
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>,
): boolean => {
  const preservedParams = ['id', 'activeTab'];
  const filters = [];

  Object.entries(query).forEach(entry => {
    if (preservedParams.includes(entry[0])) return;
    let values = (entry[1] as string)?.split(',');
    if (values[0].startsWith('http')) values = values.map(v => encodeURI(v));
    filters.push({ id: entry[0], value: values || [] });
  });

  if (filters.length === 0) {
    const stored = filterPersistenceStore.get(Router.pathname);
    if (stored?.length > 0) {
      setColumnFilters(stored);
      return true;
    }
    setColumnFilters(defaultFilters);
    return false;
  }

  setColumnFilters(filters);
  return true;
};

const getDefaultFilters = (
  urlParameters: string | undefined,
  setDefaultFilters: Dispatch<SetStateAction<ColumnFilter[]>>,
  setIsLoading: Dispatch<SetStateAction<boolean>>,
  setColumnFilters: Dispatch<SetStateAction<ColumnFiltersState>>,
) => {
  if (urlParameters === '' || urlParameters === undefined || urlParameters === null) {
    setIsLoading(false);
    setColumnFilters([]);
    return;
  }

  const filters = JSON.parse(urlParameters);
  if (filters?.length === 0) {
    setIsLoading(false);
    setColumnFilters([]);
    setDefaultFilters([]);
    return;
  }

  // Get all current query parameters
  const currentQuery = Router?.query;

  // Create new query object with all existing parameters
  const newQuery = { ...currentQuery };

  // Add filter parameters
  filters.forEach((filter: ColumnFilter) => {
    newQuery[filter.id] = (filter.value as string[]).join(',');
  });

  Router.push({ query: newQuery }, undefined, {
    shallow: true,
  });

  setColumnFilters(filters);
  setDefaultFilters(filters);
};

const saveFilters = async (
  api: ApiApi,
  query: ParsedUrlQuery,
  setDefaultFilters: Dispatch<SetStateAction<ColumnFilter[]>>,
  tableId: number,
) => {
  const filters = [];
  Object.entries(query).forEach(entry => {
    let values = (entry[1] as string)?.split(',');
    // encode URLs
    if (values[0].startsWith('http')) {
      values = values.map(v => encodeURI(v));
    }
    filters.push({ id: entry[0], value: values || [] });
  });

  setDefaultFilters(filters);
  const urlParameters = JSON.stringify(filters);
  await api.apiViewSettingsPartialUpdate({
    id: tableId,
    patchedViewSettings: { urlParameters },
  });
  viewSettingsStore.updateViewSettingsRecord(tableId, { urlParameters });

  toast.success(
    <div>
      <div className={styles.headerText}>Default filters are saved</div>
      <div className={styles.infoText}>{`We've successfully saved current applied filters as default.`}</div>
      {/* <div
        onClick={e => {
          try {
            e.stopPropagation();
            navigator.clipboard.writeText(window.location.href);
            const linkElement = document.querySelector("[class^='ModelInventoryTable_copyLink'");
            linkElement.innerHTML = 'Copied';
            (linkElement as any).style = 'color: #4d5761';
            setTimeout(() => {
              linkElement.innerHTML = 'Copy link';
              (linkElement as any).style = 'color: #628fb9';
            }, 2200);
          } catch (error) {
            console.log(error);
          }
        }}
        className={styles.copyLink}
      >
        Copy link
      </div> */}
    </div>,
    { theme: 'light' },
  );
};

export const FiltersHelper = { updateQuery, getFiltersFromUrl, getDefaultFilters, saveFilters };
